import { FC } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import { pluralizeWithCount } from "@common/helpers/string";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { CommandsDirectory, CommandsListProps } from "./lib";
import { useCommands } from "./useCommands";

export const AccrualReviewBulkCommandsList: FC<CommandsListProps> = ({
    options = {},
}) => {
    const { open, close } = useCommands();
    const {
        selectedTransactionCount = 0,
        transactionBulkEntityOptions,
        transactionBulkCounterpartyOptions,
        transactionBulkCategoryOptions,
        transactionBulkCoaOptions,
        transactionBulkAccrualCoaOptions,
        transactionBulkClassTree,
        isTransactionBulkFieldLocked,
        transactionBulkFieldLockedTooltip,
        onTransactionBulkSetMemo,
        onTransactionBulkCopyLink,
        onTransactionBulkCreateTask,
        onTransactionBulkRemoveCounterparty,
    } = options;

    const heading = `${pluralizeWithCount("transaction", selectedTransactionCount)} selected`;

    return (
        <Command.Group heading={heading}>
            {onTransactionBulkCreateTask && (
                <CommandItem
                    keywords={["task", "create"]}
                    onSelect={() => {
                        close();
                        onTransactionBulkCreateTask();
                    }}
                    shortcut="t"
                >
                    Create task
                </CommandItem>
            )}

            {transactionBulkCategoryOptions &&
                transactionBulkCategoryOptions.length > 0 && (
                    <CommandItem
                        keywords={["category", "expense", "income"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_CATEGORY,
                                options,
                            )
                        }
                        shortcut="c"
                    >
                        Set category
                    </CommandItem>
                )}

            {transactionBulkCounterpartyOptions &&
                transactionBulkCounterpartyOptions.length > 0 && (
                    <CommandItem
                        keywords={["counterparty", "vendor", "payee"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_COUNTERPARTY,
                                options,
                            )
                        }
                        shortcut="v"
                    >
                        Set counterparty
                    </CommandItem>
                )}

            {onTransactionBulkRemoveCounterparty && (
                <CommandItem
                    keywords={["remove", "counterparty"]}
                    disabled={isTransactionBulkFieldLocked}
                    disabledTooltip={transactionBulkFieldLockedTooltip}
                    onSelect={() => {
                        close();
                        onTransactionBulkRemoveCounterparty();
                    }}
                >
                    Remove counterparty
                </CommandItem>
            )}

            {transactionBulkClassTree &&
                transactionBulkClassTree.length > 0 && (
                    <CommandItem
                        keywords={["class", "tag"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_CLASS,
                                options,
                            )
                        }
                        shortcut="l"
                    >
                        Set class
                    </CommandItem>
                )}

            {transactionBulkCoaOptions &&
                transactionBulkCoaOptions.length > 0 && (
                    <CommandItem
                        keywords={[
                            "modified cash ledger",
                            "cash ledger",
                            "account",
                            "coa",
                        ]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_COA,
                                options,
                            )
                        }
                        shortcut="g"
                    >
                        Modified cash ledger
                    </CommandItem>
                )}

            {transactionBulkAccrualCoaOptions &&
                transactionBulkAccrualCoaOptions.length > 0 && (
                    <CommandItem
                        keywords={["accrual", "ledger"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_ACCRUAL_COA,
                                options,
                            )
                        }
                        shortcut="a"
                    >
                        Accrual ledger
                    </CommandItem>
                )}

            {transactionBulkEntityOptions &&
                transactionBulkEntityOptions.length > 0 && (
                    <CommandItem
                        keywords={["entity", "business", "personal"]}
                        disabled={isTransactionBulkFieldLocked}
                        disabledTooltip={transactionBulkFieldLockedTooltip}
                        onSelect={() =>
                            open(
                                CommandsDirectory.TRANSACTION_BULK_ENTITY,
                                options,
                            )
                        }
                        shortcut="e"
                    >
                        Set entity
                    </CommandItem>
                )}

            {onTransactionBulkSetMemo && (
                <CommandItem
                    keywords={["note", "memo"]}
                    onSelect={() => {
                        close();
                        onTransactionBulkSetMemo();
                    }}
                    shortcut="m"
                >
                    Set memo
                </CommandItem>
            )}

            {onTransactionBulkCopyLink && (
                <CommandItem
                    keywords={["copy", "link", "url", "share"]}
                    onSelect={() => {
                        close();
                        onTransactionBulkCopyLink();
                    }}
                >
                    Copy URL
                </CommandItem>
            )}
        </Command.Group>
    );
};
